
import {
  computed,
  defineComponent,
  ref,
  toRefs,
  onMounted,
  WritableComputedRef,
  inject,
  onUnmounted,
  nextTick,
  watch,
} from "vue";
import { SmsAPI } from "@/services/api/Sms.service";
import { ISms, IParticipants } from "@/interfaces/iSms";
import { PAGE_ITEMS, usePagination } from "@/helpers/pagination";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { TranslationService } from "@/core/services/TranslationService";
import VZModal from "@/components/content/VZModal.vue";
import { useToast } from "vue-toastification";
import { getLookups } from "@/services/api/Lookup.service";
import VZFilter from "@/components/kt-datatable/components/VZFilter.vue";
import UnReadBadge from "@/views/sms/components/list/UnReadBadge.vue";
import { hasPermission } from "@/core/services/permission.service";

interface IPagination {
  page: number;
  total: number;
  rowsPerPage: number;
  search?: string;
}
import { useRoute, useRouter } from "vue-router";
import { ErrorMessage, Field, Form } from "vee-validate";
import * as Yup from "yup";
import { useStore } from "vuex";
import { ICurrentUser } from "@/interfaces/iCurrentUser";
import { IDataTableFilterOption } from "@/interfaces/iPageModel";
import { MessageAPI } from "@/services/api/Message.service";
import { Emitter } from "mitt";
import VZMultiSelect from "@/components/modals/forms/VZMultiSelect.vue";
import { Actions } from "@/store/enums/StoreEnums";
import {
  FETCH_SMS_NOTIFICATION,
  NOTIFICATION_STORE,
} from "@/store/notification/types";
import {
  GET_CONVERSATION_CHANGED,
  MESSAGE_STORE,
  SMS_DATA_CHANGED,
  GET_PARTICIPANT_DETAIL,
  ADD_NEW_MESSAGE,
  RESET_STORE,
} from "@/store/message/types";
import SMS_POST_SUBSCRIPTION from "@/graphql/smsPostSubscription.graphql";
import { useSubscription } from "@vue/apollo-composable";
import { iParticipant } from "@/interfaces/iMessage";
interface ISMSEmit {
  webId: string | number;
}
interface Boolean {
  check: boolean;
}
type Events = {
  "sms-title-update": ISMSEmit;
  "sms-list-refresh": boolean;
};

export default defineComponent({
  name: "private-chat",
  components: {
    Form,
    Field,
    ErrorMessage,
    VZModal,
    VZFilter,
    UnReadBadge,
    VZMultiSelect,
  },
  emits: ["get-sms-details"],
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const showDialog = ref<any>(false);
    const createConversationForm = ref<null | HTMLFormElement>(null);
    const selectedForDeleteClass = ref<string>("primary");
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const toast = useToast();
    const smsData = ref<ISms[]>([]);
    const activeFilter = ref<number>(0);
    const emitter: Emitter<Events> | undefined = inject("emitter");
    const createMessage = t("pages.sms.createMessage");
    const updateMessage = t("pages.sms.updateMessage");
    const conversationValidation = Yup.object().shape({
      title: Yup.string()
        .strict(true)
        .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
        .required(t("common.name") + " " + t("common.isRequired"))
        .matches(/\S/, t("common.name") + " " + t("common.isRequired")),
      selectedUser: Yup.array()
        .nullable()
        .of(Yup.number().required())
        .min(1, t("pages.sms.createGroup.participants") + " " + "are required")
        .required(
          t("pages.sms.createGroup.participants") + " " + "are required"
        ),
    });
    const loading = ref(false);
    const localPreference = ref({});
    const searchSms = ref<string>("");
    const title = ref<string>("");
    const selectedUser = ref<number[]>([]);
    const { page, total, rowsPerPage } = usePagination();
    const allSelected = ref(false);
    const selectedForDelete = ref<number[]>([]);
    const disableButton = ref<boolean>(true);
    const PAGE_ITEM = ref<number[]>(PAGE_ITEMS);
    let options: { label: any; value: string }[] = [];
    const selectedData = ref<ISms[]>([]);
    const SMS_Changed = `${MESSAGE_STORE}/${GET_CONVERSATION_CHANGED}`;
    const tableFilter = ref<Array<IDataTableFilterOption>>([
      {
        key: "userIds",
        label: TranslationService.translate("common.users"),
        type: "lookup",
        attrs: {
          mode: "tags",
        }, //
        url: "security/filter/USERS",
        textKey: "text",
        valueKey: "value",
      },
    ]);
    const format = computed(() => {
      return FormatMethods;
    });
    const conversationId = computed(() => {
      return route?.params?.id?.toString() || 0;
    });
    const multipleDeleteSuccessMessage = t(
      "common.multipleDeleteSuccessMessage"
    );
    const pagination = ref<IPagination>({
      page: page.value,
      total: total.value,
      rowsPerPage: rowsPerPage.value,
    });
    const checkAll: WritableComputedRef<boolean> = computed({
      get(): boolean {
        return (
          (smsData.value.length > 0 &&
            smsData.value.every((item: any) => {
              return item && selectedForDelete.value.includes(item["webId"]);
            })) ??
          false
        );
      },
      set(newValue: boolean): void {
        const localSelect = smsData.value.map((item: any) => item["webId"]);
        if (newValue) {
          selectedForDelete.value = [
            ...selectedForDelete.value,
            ...localSelect.filter((c) => !selectedForDelete.value.includes(c)),
          ];
        } else {
          selectedForDelete.value = [
            ...selectedForDelete.value.filter((c) => !localSelect.includes(c)),
          ];
        }
      },
    });

    const discard = () => {
      selectedUser.value = [];
      nextTick(() => {
        createConversationForm?.value?.resetForm();
      });
    };

    const onBulkDelete = async () => {
      showDialog.value = false;
      let deletedMsg = "Selected chat group(s) " + multipleDeleteSuccessMessage;
      try {
        await SmsAPI.getInstance().bulkDeleteEntity(selectedForDelete.value);
        await Promise.all(
          selectedForDelete.value.map(
            async (webId: number) =>
              await MessageAPI.getInstance().markMessagesRead(webId)
          )
        );
        await store.dispatch(`${NOTIFICATION_STORE}/${FETCH_SMS_NOTIFICATION}`);
        selectedForDelete.value = [];
        disableButton.value = true;
        refreshList();
        toast.success(deletedMsg);
      } catch (e) {
        //
      }
    };
    const currentUser = computed(() => {
      return store.getters.currentUser as ICurrentUser;
    });
    const searchChange = () => {
      queryUpdate({
        page: 0,
        size: rowsPerPage.value,
        search: searchSms.value,
      });
    };
    const createConversation = async (values, { resetForm }) => {
      let collapseDiv = document.getElementById("createGroup") as HTMLElement;
      collapseDiv.className = "border border-gray-300 mb-3 collapse";
      try {
        const user =
          selectedUser?.value.length > 0
            ? selectedUser.value
                .filter((user) => `${user}` !== `${currentUser.value.webId}`)
                .map((user) => {
                  return {
                    webId: user,
                  };
                })
            : [];
        if (user) {
          await SmsAPI.getInstance().createConversation({
            title: title.value,
            status: "OPEN",
            clientId: currentUser.value.clientId ?? 0,
            participants: [...user, { webId: currentUser.value.webId }],
          });
          selectedUser.value = [];
          await store.dispatch(`${MESSAGE_STORE}/${SMS_DATA_CHANGED}`);
          resetForm();
          queryUpdate();
          toast.success(createMessage);
        }
      } catch (e) {
        //
      } finally {
        //
      }
    };
    const refreshList = () => {
      queryUpdate({ ...localPreference.value });
    };
    const getSmsDetails = (smsData: ISms, element?) => {
      emit("get-sms-details", smsData);
    };
    onMounted(async () => {
      loading.value = true;
      await getLookups(`security/filter/USERS`)
        .then((data: any) => {
          data.map((obj: any) => {
            options.push({
              label: obj.text,
              value: obj.value,
            });
          });
        })
        .catch((err) => {
          return [];
        });
      const isActiveUser = computed(() => {
        const key = `${MESSAGE_STORE}/${GET_PARTICIPANT_DETAIL}`;
        const participants = store.getters[key] as iParticipant[];
        return (
          participants.findIndex(
            (participant) =>
              `${participant.webId}` === `${currentUser.value.webId}` &&
              participant.status !== "INACTIVE"
          ) > -1
        );
      });
      const { result, onError } = useSubscription(
        SMS_POST_SUBSCRIPTION,
        () => ({
          conversationId: conversationId.value,
        })
      );
      onError((error) => {
        console.log(error, "smsList");
      });
      // watch(result, async (data) => {
      //   console.log("isActiveUser", isActiveUser);
      //   await store.dispatch(`${MESSAGE_STORE}/${ADD_NEW_MESSAGE}`, {
      //     conversationId: conversationId.value,
      //     message: data.smsPost,
      //     currentUser: currentUser.value.webId,
      //   });
      // });
    });
    emitter?.on("sms-title-update", (value) => {
      toast.success(updateMessage);
      refreshList();
    });
    emitter?.on("sms-list-refresh", async (value) => {
      refreshList();
      // await store.dispatch(`${MESSAGE_STORE}/${RESET_STORE}`);
    });
    onUnmounted(() => {
      emitter?.off("sms-title-update");
      emitter?.off("sms-list-refresh");
    });
    const currentPageChange = (val) => {
      queryUpdate({ ...localPreference.value, page: val - 1 });
    };
    const setItemsPerPage = () => {
      queryUpdate({
        ...localPreference.value,
        page: 0,
        size: rowsPerPage.value,
      });
    };
    const getParticipants = (participants: Array<IParticipants>) => {
      return participants.map((participant) => participant.email).join(",");
    };
    const onFilterChange = (filters) => {
      activeFilter.value =
        Object.keys(filters).filter((item) => {
          if (
            (Array.isArray(filters[item]) && !filters[item]?.length) ||
            !filters[item] ||
            filters[item] === null
          ) {
            return false;
          }
          return true;
        }).length ?? 0;
      const localFilters = Object.keys(filters)
        .filter((key) => {
          const lookup =
            (tableFilter.value &&
              tableFilter.value.findIndex(
                (tab) => tab.key === key && tab.type === "lookup"
              )) ??
            -1;
          if (lookup > -1) {
            return !filters[key].includes("-1");
          }
          return true;
        })
        .reduce((filter, key) => {
          filter[key] = filters[key];
          return filter;
        }, {});
      queryUpdate({
        page: 0,
        size: rowsPerPage.value,
        ...localFilters,
      });
    };
    const queryUpdate = async (params?: Record<string, number | string>) => {
      try {
        loading.value = true;
        localPreference.value = { ...params };
        const data = await SmsAPI.getInstance().getSmsList({
          status: "OPEN",
          sort: "createdDate,desc",
          ...params,
        });
        smsData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
        emit("get-sms-details", smsData.value[0]);
      } finally {
        selectedData.value = [];
        if (
          conversationId.value &&
          smsData.value.findIndex(
            (sms) => `${sms.webId}` === `${conversationId.value}`
          ) > -1
        ) {
          const data = smsData.value.find(
            (sms) => `${sms.webId}` === `${conversationId.value}`
          );
          if (data) {
            selectedData.value[0] = data;
            // emit("get-sms-details", data);
          }
        } else {
          if (smsData.value.length > 0) {
            selectedData.value[0] = smsData.value[0] ?? [];
            // emit("get-sms-details", selectedData.value[0]);
          } else {
            router.push({ name: "sms" });
          }
        }
        loading.value = false;
      }
    };
    watch(loading, (newValue) => {
      if (newValue) {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
      } else {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    });
    return {
      t,
      loading,
      total,
      page,
      rowsPerPage,
      format,
      searchSms,
      searchChange,
      selectedData,
      getSmsDetails,
      pagination,
      currentPageChange,
      setItemsPerPage,
      allSelected,
      selectedForDelete,
      selectedForDeleteClass,
      disableButton,
      getParticipants,
      PAGE_ITEM,
      onBulkDelete,
      multipleDeleteSuccessMessage,
      options,
      selectedUser,
      title,
      conversationValidation,
      createConversation,
      createConversationForm,
      smsData,
      checkAll,
      tableFilter,
      onFilterChange,
      activeFilter,
      discard,
      hasPermission,
      showDialog,
      conversationId,
      refreshList,
    };
  },
});
